import React, { useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Repeat } from 'lucide-react'
import SessionParticipants, { type SessionParticipant } from './SessionParticipants'
import SessionParticipantsSummary from './SessionParticipantsSummary'
import InstructorNameTag from '../features/instructors/components/InstructorNameTag'
import { Session } from '../features/sessions/models/Session'
import { RecurrenceType } from '../features/sessions/models/RecurrenceType'
import { InviteeType } from '../features/sessions/models/InviteeType'
import { formatLocalTimeRange } from '../features/utils/timeFormatter'
import { ROUTES } from '../routes'
import { useAppSelector } from '../app/hooks'
import { selectMembers, selectGroups } from '../features/members/members-slice'
import SampleBadge from '../features/sample-data/components/SampleBadge'
import { selectSampleSessionIds } from '../features/sample-data/sample-data-slice'

type SessionCardProps = {
  session: Session
  borderClass?: string
}

const SessionCard: React.FC<SessionCardProps> = ({ session, borderClass }) => {
  const { t } = useTranslation();
  const sampleSessionIds = useAppSelector(selectSampleSessionIds);
  const navigate = useNavigate();
  const location = useLocation();
  const allMembers = useAppSelector(selectMembers);
  const allGroups = useAppSelector(selectGroups);

  const handleDetailsClick = () => {
    navigate(ROUTES.SESSION_DETAILS.replace(':id', session.id));
  };

  const isRecurring = session.recurrenceType !== RecurrenceType.No;

  // A recurring session's list row is one occurrence — pass its date along so
  // the editor's "single occurrence" scope can pre-select it.
  const handleEditClick = () => {
    navigate(ROUTES.EDIT_SESSION.replace(':id', session.id), {
      state: {
        from: location.pathname,
        occurrenceDate: isRecurring ? session.dateStart.slice(0, 10) : undefined,
      }
    });
  };

  // Format times using timezone-aware utility
  const timeRange = formatLocalTimeRange(session.timeLocal, session.duration);
  const times = [timeRange];

  // Map invitees to participants with resolved names and tooltip data.
  // Group-type invitees come in two shapes: legacy rows where invitedUserId is a
  // group id, and per-member rows where it is a member id (Type=Group only records
  // provenance) — the latter render as that member.
  const participants: SessionParticipant[] = useMemo(() => {
    const memberMap = new Map(allMembers.map((m) => [m.id, m]));
    const groupMap = new Map(allGroups.map((g) => [g.id, g]));

    return session.invitees.map(invitee => {
      if (invitee.type === InviteeType.Group && !memberMap.has(invitee.invitedUserId)) {
        const group = groupMap.get(invitee.invitedUserId);
        const groupMembers = allMembers.filter(m =>
          m.groups.some(g => g.id === invitee.invitedUserId)
        );
        return {
          name: group?.name ?? `Group ${invitee.invitedUserId.substring(0, 8)}`,
          type: 'group' as const,
          count: groupMembers.length,
          tooltipLines: groupMembers.length > 0
            ? groupMembers.map(m => `${m.firstName} ${m.lastName}`)
            : ['No members'],
        };
      } else {
        const member = memberMap.get(invitee.invitedUserId);
        const name = member
          ? `${member.firstName} ${member.lastName}`
          : t("sessions.formerMember", "Former member");
        return {
          name,
          type: 'individual' as const,
          tooltipLines: [name],
        };
      }
    });
  }, [session.invitees, allMembers, allGroups, t]);

  // Determine variant based on session id
  const variant = session.id.length % 2 === 0 ? 'orange' : 'aqua';

  const variantClass = variant === 'aqua' ? 'border-aqua' : variant === 'orange' ? 'border-orange' : ''
  const combinedBorderClass = [variantClass, borderClass].filter(Boolean).join(' ').trim()
  return (
    <div className="with-border flex overflow-hidden rounded-2xl cursor-pointer transition-colors hover:bg-grey-95 dark:hover:bg-primary-20">
      <div className="flex w-full flex-col gap-2 md:flex-row md:items-center">
        <div className={`event-details-info m-0 flex w-full flex-col gap-2 border-l-[8px] px-2 py-3 md:grid md:grid-cols-[minmax(0,7fr)_minmax(150px,4fr)_minmax(0,5fr)_auto] md:items-center md:gap-4 md:p-4 ${combinedBorderClass}`.trim()}>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <p
                className="line-clamp-1 font-semibold cursor-pointer hover:underline"
                onClick={handleDetailsClick}
              >
                {session.title}
              </p>
              {sampleSessionIds.includes(session.id) && <SampleBadge />}
            </div>
            {session.description && (
              <p className="line-clamp-1 hidden text-sm text-grey-40 dark:text-grey-60 md:block">
                {session.description}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 md:flex-col md:items-start md:gap-0">
            {times.slice(0, 2).map((t, idx) => (
              <p
                key={idx}
                className="text-grey-30 dark:text-grey-70 border-r-grey-70 line-clamp-1 max-w-fit border-r pr-2 text-sm last:border-r-0 md:border-r-0 md:pr-0"
              >
                {t}
              </p>
            ))}
            {isRecurring && (
              <p
                className="flex items-center gap-1 text-xs text-grey-40 dark:text-grey-60"
                title={t("sessions.recurringSeries")}
              >
                <Repeat size={12} aria-label={t("sessions.recurringSeries")} />
                <span>{t("sessions.seriesBadge")}</span>
              </p>
            )}
            {/* Assigned instructor — renders only in a multi-instructor organization */}
            <InstructorNameTag instructorId={session.instructorId} />
          </div>
          <div className="min-w-0">
            {participants.length > 0 ? (
              <SessionParticipants participants={participants} />
            ) : null}
          </div>
          <SessionParticipantsSummary session={session} className="md:justify-self-end" />
        </div>
      </div>
      <div className="border-l-grey-70 flex h-auto w-fit items-center justify-center border-l md:px-2">
        <button onClick={handleEditClick} className="btn btn-text h-full px-2">
          <svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
              fill="#4D4D4D"
            />
          </svg>
          <span className="hidden md:block">{t("common.edit")}</span>
        </button>
        <div className="border-l-grey-70 border-l w-px h-8"></div>
        <button onClick={handleDetailsClick} className="btn btn-text h-full px-2">
          <span className="hidden md:block">{t("common.details")}</span>
          <svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.75781 7.03029C10.1043 6.93744 10.4749 7.03639 10.7285 7.29005L14.6982 11.2901C14.8875 11.4778 14.9941 11.7334 14.9941 12C14.9941 12.2666 14.8875 12.5222 14.6982 12.71L10.6982 16.71C10.512 16.8947 10.2604 16.9989 9.99805 17C9.73224 17.0015 9.4768 16.8972 9.28809 16.71C9.0988 16.5222 8.99219 16.2666 8.99219 16C8.99221 15.7334 9.09879 15.4778 9.28809 15.2901L12.5781 12L9.30762 8.70997C9.05419 8.45635 8.95509 8.08659 9.04785 7.74025C9.14067 7.39386 9.41148 7.12322 9.75781 7.03029Z"
              fill="#4D4D4D"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default SessionCard
